/* eslint-disable camelcase */
import type { SurveyQuestion } from '@hotjar/feedback/domain/Surveys/Questions';
import type { CreatedByUser } from '@hotjar/feedback/domain/common/CreatedByUser';
import type { DisplayType } from '@hotjar/webapp/features/Surveys/domain/DisplayType';

export const SURVEY_NAME_MAX_LENGTH = 50;

export type DeviceTarget = {
  component: 'device';
  match_operation: 'exact';
  pattern: 'desktop' | 'tablet' | 'phone';
};

export type TriggerTarget = {
  component: 'trigger';
  match_operation: 'exact';
  pattern: string;
};

export type UrlTarget = {
  component: 'url';
  match_operation: 'simple' | 'exact' | 'starts_with' | 'ends_with' | 'contains' | 'regex';
  negate: boolean;
  pattern: string;
};

export type ExactNumberAttributeTarget = {
  component: 'attribute';
  match_operation: 'exact';
  negate: boolean;
  pattern: string;
  rule_type: 'number';
  name: string;
};

export type UnknownNumberAttributeTarget = {
  component: 'attribute';
  match_operation: 'unknown';
  negate: boolean;
  rule_type: 'number';
  name: string;
  pattern: boolean;
};

export type NumberAttributeTarget =
  | ExactNumberAttributeTarget
  | UnknownNumberAttributeTarget
  | {
      component: 'attribute';
      match_operation: 'greater_than' | 'less_than';
      pattern: string;
      rule_type: 'number';
      name: string;
    };

export type UnknownBooleanAttributeTarget = {
  component: 'attribute';
  match_operation: 'unknown';
  negate: boolean;
  rule_type: 'boolean';
  name: string;
  pattern: boolean;
};

export type BooleanAttributeTarget =
  | UnknownBooleanAttributeTarget
  | {
      component: 'attribute';
      match_operation: 'exact';
      pattern: 'true' | 'false';
      rule_type: 'boolean';
      name: string;
    };

export type RelativeDateAttributeTarget = {
  component: 'attribute';
  match_operation: 'exact_days_ago' | 'more_than_days_ago' | 'less_than_days_ago';
  pattern: number;
  rule_type: 'date';
  name: string;
};

export type AbsoluteDateAttributeTarget = {
  component: 'attribute';
  match_operation: 'exact_date' | 'greater_than' | 'less_than';
  pattern: number;
  rule_type: 'date';
  name: string;
};

export type UnknownDateAttributeTarget = {
  component: 'attribute';
  match_operation: 'unknown';
  negate: boolean;
  rule_type: 'date';
  name: string;
};

export type DateAttributeTarget =
  | RelativeDateAttributeTarget
  | AbsoluteDateAttributeTarget
  | UnknownDateAttributeTarget;

export type UnknownTextAttributeTarget = {
  component: 'attribute';
  match_operation: 'unknown';
  negate: boolean;
  rule_type: 'string';
  name: string;
  pattern: boolean;
};

export type ContainsTextAttributeTarget = {
  component: 'attribute';
  match_operation: 'contains';
  negate: boolean;
  pattern: string;
  rule_type: 'string';
  name: string;
};

export type ExactTextAttributeTarget = {
  component: 'attribute';
  match_operation: 'exact';
  negate: boolean;
  pattern: string;
  rule_type: 'string';
  name: string;
};

export type PartialTextAttributeTarget = {
  component: 'attribute';
  match_operation: 'starts_with' | 'ends_with';
  pattern: string;
  rule_type: 'string';
  name: string;
};

export type TextAttributeTarget =
  | ExactTextAttributeTarget
  | UnknownTextAttributeTarget
  | PartialTextAttributeTarget
  | ContainsTextAttributeTarget;

export type AttributeTarget =
  | NumberAttributeTarget
  | BooleanAttributeTarget
  | DateAttributeTarget
  | TextAttributeTarget;

export type PageTarget = UrlTarget | TriggerTarget;

export type SurveyTargeting = DeviceTarget | PageTarget | AttributeTarget;

interface SurveyInvite {
  title: string;
  description: string;
  button: string;
  close: string;
}

export type ConnectVisitData = 'always' | 'ask' | 'never';

export interface Survey {
  connect_visit_data: ConnectVisitData;
  show_legal: boolean;
  show_branding: true;
  enabled: boolean;
  name: string;
  position: string;
  skin: string;
  background: string;
  button_color: string;
  is_custom_background_color_selected: boolean;
  is_custom_button_color_selected: boolean;
  language: string;
  content: {
    thankyou: string;
    questions: SurveyQuestion[];
  };
  invite_enabled: boolean;
  invite: SurveyInvite;
  display_condition: 'immediate' | 'delay' | 'abandon' | 'scroll';
  display_delay: number;
  persist_condition: 'response' | 'once' | 'always';
  targeting_percentage: number;
  targeting: SurveyTargeting[];
  created_epoch_time: number;
  description: string | null;
  display_type: DisplayType;
  id: number;
  response_count: number;
  uuid: string;
  created_by_user: CreatedByUser;
  forwarding_email_address_list: string[];
}

export interface SurveyListItem
  extends Pick<
    Survey,
    'id' | 'uuid' | 'created_epoch_time' | 'display_type' | 'enabled' | 'name' | 'response_count'
  > {
  created_by_user_name: string | null;
}
